import { faCartShopping, faHourglass } from '@fortawesome/pro-regular-svg-icons';
import { Banner, Checkbox, Dropdown, PrimaryButton, Tooltip } from 'component-library';
import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppContext } from '../../../contexts/AppContext';
import { Feature, Package, SystemDetail } from '../../../models/ApiModels';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CalculateMinutesToDays } from '../../../helpers/Custom';
import PermissionGate, { ROLES } from '../../../utils/PermissionGate';
import FunctionTrials from './FunctionTrials/FunctionTrials';

type Props = {
  system: SystemDetail;
  setSystem: (system: SystemDetail) => void;
  editMode?: boolean;
  showUpgradeLicense?: boolean;
  setShowUpgradeLicense: (value: boolean) => void;
  openFunctionTrialPopup: boolean;
  setOpenFunctionTrialPopup: (value: boolean) => void;
  topRef: any;
  GetSystem: () => void;
};

const SystemDetailsSoftware = (props: Props) => {
  const { isHumphreeUser } = useContext(AppContext);
  const [showBanner, setShowBanner] = useState(true);
  const packageTable = useSelector((state: any) => state.configurations.packages);
  const addonFeatures: Feature[] = useSelector((state: any) => state.configurations.features);
  const manualPackageOptionInDropdown = { id: '', value: 'Manual' };

  const hasUpgrades = useMemo(() => {
    if (props.system.packageId === '') return true; // Manual package, has upgrade

    if (!props.system.addonFeatureIds.includes('FEAT-7-1')) return true; // Underwater lights not included, has upgrade

    const currentPackage: Package = packageTable.find((pack: Package) => pack.id === props.system.packageId);
    if (currentPackage && currentPackage.availableUpgrades.length > 0) return true; // Available upgrades exist

    return false; // No upgrades
  }, [props.system.packageId, props.system.addonFeatureIds, packageTable]);

  const goToShowUpgrades = () => {
    props.setShowUpgradeLicense(true);
    scrollToTop();
  };

  const scrollToTop = () => {
    props.topRef?.current?.scrollIntoView({ behavior: 'auto' });
  };

  const hasActiveTrial = useMemo(() => {
    if (
      props.system.trialExpirationDate !== null &&
      new Date(props.system.trialExpirationDate as any).getTime() - new Date().getTime() > 0
    )
      return true;
    else return false;
  }, [props.system, props.system.trialExpirationDate]);

  return (
    <div className='flex flex-col'>
      <div className='border-b border-gray-10 pb-5'>
        <h1 className='prose-heading4 flex justify-between'>Humphree license</h1>
      </div>
      <PermissionGate roles={[ROLES.admin]} rejected={undefined}>
        {props.editMode && showBanner && (
          <div className='mt-5 w-full'>
            <Banner
              icon={true}
              warning={false}
              text={
                'Changing the license will manually override the current license. The customer will not receive an invoice. Use the upgrade license function to upgrade system.'
              }
              color={'Super Light Blue'}
              dismissBanner={() => setShowBanner(!showBanner)}
            />
          </div>
        )}
      </PermissionGate>
      <div>
        <div className='flex justify-between items-center border-b border-gray-10 py-5'>
          <h4 className='prose-paragraphBase flex gap-2 w-full'>
            {!props.system?.packageId ? 'No license added' : 'Function package'}
            <Tooltip
              description={
                !props.system?.packageId
                  ? 'No license added'
                  : 'Function packages includes automatic functions that can be selected to get the preferred level of automation to a boat. The available function packages are Comfort, Comfort Plus, Dynamic and Dynamic Plus.'
              }
            />
          </h4>
          <span className='w-[250px] text-right'>
            {props.editMode ? (
              <div className='w-[250px]'>
                <PermissionGate roles={[ROLES.admin]} rejected={undefined}>
                  <Dropdown
                    label={''}
                    optionsData={[
                      ...packageTable.map((p: Package) => {
                        return { id: p.id, value: p.displayName };
                      }),
                      manualPackageOptionInDropdown,
                    ]}
                    onValueChosen={(value: string) => {
                      props.system.packageId = value;
                      props.setSystem(props.system);
                    }}
                    placeholder={''}
                    disabled={!isHumphreeUser}
                    value={`${
                      props.system?.packageId
                        ? packageTable.find((packages: Package) => packages.id === props.system?.packageId)?.displayName
                        : 'Manual'
                    }`}
                  />
                </PermissionGate>
              </div>
            ) : (
              <div>
                {props.system?.packageId
                  ? packageTable.find((packages: Package) => packages.id === props.system?.packageId)?.displayName
                  : 'Manual'}
              </div>
            )}
          </span>
        </div>
        <div className='flex justify-between items-center border-b border-gray-10 py-5 w-full'>
          <h4 className='prose-paragraphBase flex gap-2 w-full'>
            Additional features
            <Tooltip description='Features that can be additionally selected to a license, such as underwater lights.' />
          </h4>
          {(props.editMode || props.system.addonFeatureIds.length > 0) && (
            <div className='flex flex-row w-full gap-5 justify-end'>
              {addonFeatures.map((addonFeature: Feature) => {
                return (
                  addonFeature.addon && (
                    <div key={addonFeature.id} className='flex flex-row items-center gap-4'>
                      {props.editMode && (
                        <PermissionGate roles={[ROLES.admin]} rejected={undefined}>
                          <Checkbox
                            active={props.system.addonFeatureIds.some((id: string) => id === addonFeature.id)}
                            disabled={!isHumphreeUser}
                            onClick={() => {
                              const index = props.system.addonFeatureIds.findIndex(
                                (id: string) => id === addonFeature.id
                              );
                              if (index > -1) {
                                props.system.addonFeatureIds.splice(index, 1);
                              } else {
                                props.system.addonFeatureIds.push(addonFeature.id);
                              }
                              props.setSystem(props.system);
                            }}
                          />
                        </PermissionGate>
                      )}
                      <h2 className='prose-paragraphBase'>{addonFeature.displayName}</h2>
                    </div>
                  )
                );
              })}
            </div>
          )}
        </div>

        {hasUpgrades && (
          <div className='flex justify-between items-center border-b border-gray-10 py-5 w-full'>
            <h4 className='prose-paragraphBase flex gap-2 w-full'>
              Function trial days
              <Tooltip description='Remaining days that can be used to activate function trials' />
            </h4>
            <div className='flex w-full justify-end'>
              {(props.system?.trialRemainingDays && CalculateMinutesToDays(props.system?.trialRemainingDays)) || 0} days
              remaining
            </div>
          </div>
        )}
      </div>
      <PermissionGate roles={[ROLES.admin, ROLES.editor, ROLES.oemAdmin]} rejected={undefined}>
        <div className='flex flex-wrap gap-2 justify-end pt-5 items-center prose-buttonStandard'>
          {hasUpgrades &&
            (!hasActiveTrial ? (
              <PrimaryButton
                label='Activate function trial'
                icon={faHourglass}
                onClick={() => props.setOpenFunctionTrialPopup(true)}
              />
            ) : (
              <div
                onClick={() => isHumphreeUser && props.setOpenFunctionTrialPopup(true)}
                className='bg-[#E29FE3] text-[#622863] h-10 flex justify-center items-center px-4 gap-2 cursor-pointer'
              >
                <FontAwesomeIcon icon={faHourglass} />
                <span>Function trial in progress</span>
              </div>
            ))}
          {hasUpgrades && (
            <PrimaryButton label='Buy available upgrades' icon={faCartShopping} onClick={() => goToShowUpgrades()} />
          )}
        </div>
      </PermissionGate>
      {props.openFunctionTrialPopup && (
        <FunctionTrials
          GetSystem={props.GetSystem}
          system={props.system}
          setOpenFunctionTrialPopup={props.setOpenFunctionTrialPopup}
        />
      )}
    </div>
  );
};

export default SystemDetailsSoftware;
